import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import DOMPurify from 'dompurify';
import api from '../lib/api';
import { Trophy, X, BookOpen } from 'lucide-react';
import './CourseCompletionCelebration.css';

/** Lavish "ختم المساق" celebration modal with Omani-Islamic scholarly aesthetic.
 *  Reads its template text from `db.achievement_templates` (event_type =
 *  `course_completed`), which admins can edit via the existing Achievement
 *  Templates page. Falls back to a warm default if none exists.
 *
 *  Props:
 *    open        — boolean
 *    onClose     — () => void
 *    courseTitle — string, used to fill {course_title}
 *    readerName  — string, used to fill {reader_name}
 */
const DEFAULT_TEMPLATE = {
  title: 'تهانينا! أتممت المساق بنجاح',
  message:
    'بسم الله الرحمن الرحيم\n\n' +
    '{reader_name}، ما شاء الله تبارك الله!\n\n' +
    'لقد أتممت مساق «{course_title}» بحمد الله وتوفيقه.\n\n' +
    'قال الإمام أحمد بن حنبل رحمه الله:\n' +
    '«الناس إلى العلم أحوج منهم إلى الطعام والشراب»\n\n' +
    'نفع الله بك وبعلمك، وجعله في ميزان حسناتك.',
};

const applyTokens = (str, tokens) => {
  let out = str || '';
  Object.entries(tokens || {}).forEach(([k, v]) => {
    out = out.split(`{${k}}`).join(String(v ?? ''));
  });
  return out;
};

const CourseCompletionCelebration = ({ open, onClose, courseTitle, readerName, overrideTemplate }) => {
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Admin preview path — use the in-flight edited template directly so the
    // preview reflects unsaved changes without hitting the API.
    if (overrideTemplate) { setTemplate(overrideTemplate); return; }
    (async () => {
      try {
        const res = await api.get('/achievement-templates/course_completed');
        setTemplate(res.data || DEFAULT_TEMPLATE);
      } catch {
        setTemplate(DEFAULT_TEMPLATE);
      }
    })();
  }, [open, overrideTemplate]);

  const tokens = { reader_name: readerName || 'أخي القارئ', course_title: courseTitle || '' };
  const title = applyTokens(template?.title || DEFAULT_TEMPLATE.title, tokens);
  const messageHtml = applyTokens(template?.message || DEFAULT_TEMPLATE.message, tokens)
    .split('\n')
    .map((line) => `<p class="ccc-line">${line || '&nbsp;'}</p>`)
    .join('');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl bg-transparent border-none shadow-none p-0"
        data-testid="course-completion-celebration"
        hideClose
      >
        <div className="ccc-frame">
          {/* Decorative arabesque corners */}
          <span className="ccc-corner ccc-corner-tl" aria-hidden />
          <span className="ccc-corner ccc-corner-tr" aria-hidden />
          <span className="ccc-corner ccc-corner-bl" aria-hidden />
          <span className="ccc-corner ccc-corner-br" aria-hidden />

          {/* Falling gold particles */}
          <div className="ccc-particles" aria-hidden>
            {Array.from({ length: 18 }).map((_, i) => (
              <span key={i} className={`ccc-p ccc-p-${i % 6}`} style={{ ['--i']: i }} />
            ))}
          </div>

          <button className="ccc-close" onClick={onClose} aria-label="إغلاق" data-testid="ccc-close">
            <X className="w-5 h-5" />
          </button>

          <div className="ccc-body">
            <div className="ccc-badge">
              <Trophy className="w-9 h-9" />
              <span className="ccc-badge-glow" aria-hidden />
            </div>

            <p className="ccc-eyebrow">بحمد الله وتوفيقه</p>
            <h2 className="ccc-title">{title}</h2>

            {courseTitle && (
              <div className="ccc-course">
                <BookOpen className="w-4 h-4" />
                <span>{courseTitle}</span>
              </div>
            )}

            <div
              className="ccc-message answer-html"
              dir="rtl"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(messageHtml) }}
            />

            <Button
              className="ccc-cta"
              onClick={onClose}
              data-testid="ccc-continue"
            >
              الحمد لله على التوفيق
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CourseCompletionCelebration;
